import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { Fonts } from "../../constants/AppEnums";
import AppDialog from "../../components/AppBase/AppDialog";
import { showMessage } from "../../redux/actions/Common";
import { addUserToRisOrg } from "../../redux/actions/RisAdmin";
import { theme } from "../../themes/theme";
import { RisOrg } from "../../types/models/RisOrg";
import { RisOrgUser } from "../../types/models/RisOrgUser";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_NAME_LENGTH = 50;

interface AddUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdded: (user: RisOrgUser) => void;
  orgs: RisOrg[];
  /** Org to preselect */
  orgId?: string;
}

/**
 * Adds a user to an org by email. Someone who has never logged in is created as a pending user
 * and linked to the org when they first sign in.
 */
const AddUserDialog: React.FC<AddUserDialogProps> = ({ isOpen, onClose, onAdded, orgs, orgId }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    if (isOpen) {
      setSelectedOrgId(orgId ?? (orgs.length === 1 ? orgs[0].Id : ""));
      setEmail("");
      setFirstName("");
      setLastName("");
      setErrors({});
    }
  }, [isOpen]);

  const selectedOrg = orgs.find((org) => org.Id === selectedOrgId);

  const validate = () => {
    const trimmedEmail = email.trim().toLowerCase();
    const newErrors: Record<string, string | undefined> = {};
    if (!selectedOrg) {
      newErrors.org = "Please choose an organisation";
    }
    if (!emailPattern.test(trimmedEmail)) {
      newErrors.email = "Please enter a valid email";
    } else if (selectedOrg?.EmailSuffix && !trimmedEmail.endsWith(`@${selectedOrg.EmailSuffix}`)) {
      newErrors.email = `Only @${selectedOrg.EmailSuffix} emails can be added to ${selectedOrg.Name}`;
    }
    if (firstName.trim().length > MAX_NAME_LENGTH) {
      newErrors.firstName = `Must be ${MAX_NAME_LENGTH} characters or fewer`;
    }
    if (lastName.trim().length > MAX_NAME_LENGTH) {
      newErrors.lastName = `Must be ${MAX_NAME_LENGTH} characters or fewer`;
    }
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const handleSubmit = () => {
    if (!validate() || !selectedOrg) return;
    setIsSubmitting(true);
    dispatch(
      addUserToRisOrg(
        selectedOrg.Id,
        {
          Email: email.trim().toLowerCase(),
          FirstName: firstName.trim() || undefined,
          LastName: lastName.trim() || undefined,
        },
        (user) => {
          setIsSubmitting(false);
          if (user) {
            dispatch(
              showMessage(
                user.Pending
                  ? `${user.Email} has been added to ${selectedOrg.Name}. They'll be linked when they first sign in.`
                  : `${user.Email} has been added to ${selectedOrg.Name}.`
              )
            );
            onAdded(user);
          }
        }
      )
    );
  };

  return (
    <AppDialog
      open={isOpen}
      dividers
      disableBackDropOffClose
      title={<Typography sx={{ mb: 1, fontWeight: Fonts.SEMI_BOLD }}>Add User</Typography>}
      onClose={onClose}
    >
      <Box sx={{ position: "relative" }}>
        {isSubmitting && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <CircularProgress size="3rem" sx={{ color: theme.palette.primary.main }} />
          </Box>
        )}
        {orgs.length > 1 && (
          <Box sx={{ px: 2, py: 1 }}>
            <FormControl fullWidth required error={!!errors.org}>
              <InputLabel id="add-user-org-label">Organisation</InputLabel>
              <Select
                labelId="add-user-org-label"
                label="Organisation"
                value={selectedOrgId}
                onChange={(event) => setSelectedOrgId(event.target.value)}
              >
                {orgs.map((org) => (
                  <MenuItem key={org.Id} value={org.Id}>
                    {org.Name}
                  </MenuItem>
                ))}
              </Select>
              {errors.org && <FormHelperText>{errors.org}</FormHelperText>}
            </FormControl>
          </Box>
        )}
        <Box sx={{ px: 2, py: 1 }}>
          <TextField
            required
            fullWidth
            id="AddUserEmail"
            label="Email"
            type="email"
            value={email}
            placeholder={selectedOrg?.EmailSuffix ? `name@${selectedOrg.EmailSuffix}` : undefined}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setEmail(event.target.value)}
            error={!!errors.email}
            helperText={
              errors.email ??
              "If they've never signed in, they'll be added as pending until they do."
            }
          />
        </Box>
        <Stack direction="row" spacing={2} sx={{ px: 2, py: 1 }}>
          <TextField
            fullWidth
            id="AddUserFirstName"
            label="First name"
            value={firstName}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setFirstName(event.target.value)}
            error={!!errors.firstName}
            helperText={errors.firstName ?? "Optional"}
          />
          <TextField
            fullWidth
            id="AddUserLastName"
            label="Last name"
            value={lastName}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setLastName(event.target.value)}
            error={!!errors.lastName}
            helperText={errors.lastName ?? "Optional"}
          />
        </Stack>
        <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end", my: 2, px: 2 }}>
          <Button
            variant="contained"
            disabled={isSubmitting}
            onClick={handleSubmit}
            sx={{ minWidth: 100, color: "white", textTransform: "none" }}
          >
            Add User
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ minWidth: 100, textTransform: "none" }}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    </AppDialog>
  );
};

export default AddUserDialog;
